import React from "react";
import {
    Crown,
    Download,
    Mic,
    Radio,
    Sparkles,
    ArrowLeft,
    Mail,
    Calendar,
    ExternalLink,
    Copy,
    Check,
    Heart,
} from "lucide-react";
import { toast } from "sonner";
import { Recognition } from "../components/Recognition";
import { BookingForm } from "../components/BookingForm";
import { SpeakingTopics } from "../components/SpeakingTopics";

const trackDownload = (asset, label) => {
    try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/download`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ asset, label }),
            keepalive: true,
        });
    } catch (_) {}
};

const COVER_URL =
    "/assets/podcast-cover.png";
const HEADSHOT_URL =
    "/assets/coach-melissa-headshot.jpg";
const SITE_URL = "https://becomingherpod.org";
const CONTACT_EMAIL = "melissa@walkwithpurposeco.site";

const bio = {
    short:
        "Coach Melissa is a Presidential Lifetime Achievement Award recipient, faith-based life coach, and host of Becoming H.E.R.: Breaking the Cycles — a podcast for women ready to heal generational patterns, restore their identity, and walk boldly into the woman God created them to be.",
    long:
        "Coach Melissa is the founder of Unshakable Purpose LLC, a Presidential Lifetime Achievement Award recipient (2023, signed by President Joseph R. Biden, Jr.), and the heart behind Unshakable Purpose: Breaking the Cycles. She was featured in Phenomenal Overcomer Magazine's 'Set Free from the Shackles of Bondage' issue (December 2021), named one of the Hoinser Queens Book Top 100 Inspirational Women (2022), and spotlighted in Heidens Magazine's 'Your Brand · Powerful Impact' issue (April 2023). She is a contributing author to the Embrace Your Cake anthology by Sisters Inspire Sisters Network, a Room2Breathe podcast guest, and a sought-after speaker — most recently on 'Revival' at Fellowship For Christ Ministries' Evangelist Training Conference and at the Sisters Inspire Sister Speaking Conference. Blending scripture, coaching frameworks, and unfiltered testimony, she creates conversations that feel less like content and more like a soft altar call for the sister still shrinking.",
};

const stats = [
    { label: "Presidential Award", value: "2023" },
    { label: "Media features", value: "5+" },
    { label: "Episodes · Season 1", value: "6" },
    { label: "Cadence", value: "Tuesdays" },
];

const CopyableField = ({ label, value, testId }) => {
    const [copied, setCopied] = React.useState(false);
    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            toast.success(`${label} copied`);
            setTimeout(() => setCopied(false), 1800);
        } catch (_) {
            toast.error("Couldn't copy — long-press the text instead");
        }
    };
    return (
        <button
            onClick={onCopy}
            data-testid={testId}
            className="group w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-lilac-200 hover:border-gold text-left transition-colors"
        >
            <span className="grid place-items-center w-9 h-9 rounded-full bg-lilac-100 text-lilac-900 shrink-0">
                {copied ? <Check className="w-4 h-4 text-green-700" /> : <Copy className="w-4 h-4 text-gold-deep" />}
            </span>
            <span className="flex-1 min-w-0">
                <span className="block text-[10px] uppercase tracking-[0.18em] text-lilac-700 font-bold">{label}</span>
                <span className="block text-sm text-lilac-900 font-medium truncate">{value}</span>
            </span>
        </button>
    );
};

const AssetCard = ({ title, subtitle, href, thumbnail, ratio = "square", assetKey, testId }) => (
    <a
        href={href}
        download
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => assetKey && trackDownload(assetKey, title)}
        data-testid={testId}
        className="group block rounded-3xl overflow-hidden bg-white border border-lilac-200 hover:border-gold hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(43,29,53,0.15)] transition-all"
    >
        <div className={`relative overflow-hidden ${ratio === "square" ? "aspect-square" : "aspect-[4/5]"}`}>
            <img
                src={thumbnail}
                alt={title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
            <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/95 text-lilac-900 text-[10px] font-bold uppercase tracking-[0.18em]">
                <Download className="w-3 h-3 text-gold-deep" /> Save
            </span>
        </div>
        <div className="px-5 py-4">
            <div className="font-display text-lg text-lilac-900 leading-tight">{title}</div>
            <div className="text-xs text-lilac-700 mt-1">{subtitle}</div>
        </div>
    </a>
);

export const PressKit = () => {
    const [selectedTopic, setSelectedTopic] = React.useState("");
    const selectTopic = (title) => {
        setSelectedTopic(title);
        document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
    };
    return (
        <div className="min-h-screen bg-[#FDFBFD] text-lilac-900" data-testid="press-kit-page">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#FDFBFD]/85 backdrop-blur border-b border-lilac-200/60">
                <div className="mx-auto max-w-6xl px-6 md:px-10 py-4 flex items-center justify-between">
                    <a
                        href="/"
                        data-testid="press-kit-home-link"
                        className="inline-flex items-center gap-2 text-sm text-lilac-700 hover:text-lilac-900 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" /> becomingherpod.org
                    </a>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold text-white text-[10px] font-bold tracking-[0.2em] uppercase shadow-[0_6px_18px_rgba(212,175,55,0.35)]">
                        <Crown className="w-3 h-3" /> Press Kit
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="mx-auto max-w-6xl px-6 md:px-10 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                <div className="lg:col-span-7">
                    <div className="inline-flex items-center gap-2 eyebrow text-gold-deep">
                        <Sparkles className="w-3.5 h-3.5" /> For media · Interviews · Collaborations
                    </div>
                    <h1 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                        Becoming <em className="italic gold-text">H.E.R.</em>
                        <br />
                        Press Kit.
                    </h1>
                    <p className="mt-6 text-lilac-700 leading-relaxed max-w-xl">
                        Everything you need to book, interview, or feature Coach Melissa — bio, headshots,
                        cover art, and quick links. Take what serves the story. 💜
                    </p>

                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl">
                        {stats.map((s) => (
                            <div
                                key={s.label}
                                className="rounded-2xl bg-white border border-lilac-200 p-4"
                                data-testid={`press-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                <div className="font-display text-2xl gold-text leading-none">{s.value}</div>
                                <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-lilac-700 font-bold">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row gap-3">
                        <a
                            href="#booking"
                            data-testid="press-kit-book-btn"
                            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-lilac-900 hover:bg-lilac-700 text-white font-semibold text-sm shadow-[0_14px_36px_rgba(43,29,53,0.20)] transition-all"
                        >
                            <Mail className="w-4 h-4" /> Book Coach Melissa
                        </a>
                        <a
                            href="/#coaching"
                            data-testid="press-kit-discovery-btn"
                            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-gold hover:bg-gold-deep text-white font-semibold text-sm shadow-[0_14px_36px_rgba(212,175,55,0.35)] transition-all"
                        >
                            <Calendar className="w-4 h-4" /> Discovery call
                        </a>
                    </div>
                </div>

                {/* Hero cover art */}
                <div className="lg:col-span-5">
                    <div className="relative rounded-[36px] overflow-hidden border border-gold/40 shadow-[0_40px_80px_rgba(43,29,53,0.18)]">
                        <img
                            src={COVER_URL}
                            alt="Becoming H.E.R. Podcast cover — crown-and-mic art"
                            className="w-full h-auto block"
                        />
                    </div>
                    <p className="mt-4 text-center text-xs italic text-lilac-700">
                        &ldquo;Real conversations · Raw healing · Kingdom transformation.&rdquo;
                    </p>
                </div>
            </section>

            {/* Bio */}
            <section className="mx-auto max-w-6xl px-6 md:px-10 pb-14">
                <div className="rounded-[32px] bg-gradient-to-br from-[#FFF7E5] via-white to-[#FAF5FC] border border-gold/30 p-8 md:p-12">
                    <div className="eyebrow text-gold-deep flex items-center gap-2">
                        <Mic className="w-3.5 h-3.5" /> About the host
                    </div>
                    <h2 className="mt-4 font-display text-3xl md:text-4xl">
                        Meet <em className="italic gold-text">Coach Melissa</em>.
                    </h2>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
                        <div className="md:col-span-1">
                            <img
                                src={HEADSHOT_URL}
                                alt="Coach Melissa — headshot"
                                className="w-full aspect-[4/5] object-cover rounded-3xl border border-lilac-200"
                            />
                        </div>
                        <div className="md:col-span-2 space-y-5 text-lilac-900 leading-relaxed">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-gold-deep font-bold mb-2">
                                    Short bio · 40 words
                                </div>
                                <p>{bio.short}</p>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-gold-deep font-bold mb-2">
                                    Long bio · 130 words
                                </div>
                                <p className="text-lilac-700">{bio.long}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Recognition & features */}
            <Recognition />

            {/* Downloadable assets */}
            <section className="mx-auto max-w-6xl px-6 md:px-10 pb-14">
                <div className="flex items-end justify-between gap-6 mb-6">
                    <div>
                        <div className="eyebrow text-gold-deep flex items-center gap-2">
                            <Download className="w-3.5 h-3.5" /> Brand assets
                        </div>
                        <h2 className="mt-3 font-display text-3xl md:text-4xl">
                            Download <em className="italic gold-text">everything</em>.
                        </h2>
                    </div>
                    <p className="hidden md:block text-sm text-lilac-700 max-w-xs italic">
                        High-resolution files, ready for print and social. Use freely for interviews + features.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <AssetCard
                        title="Podcast cover art"
                        subtitle="2500 × 2500 · PNG · Spotify + Apple ready"
                        href={COVER_URL}
                        thumbnail={COVER_URL}
                        ratio="square"
                        testId="press-asset-cover"
                    />
                    <AssetCard
                        title="Host headshot — Coach Melissa"
                        subtitle="High-resolution · PNG · Portrait"
                        href={HEADSHOT_URL}
                        thumbnail={HEADSHOT_URL}
                        ratio="portrait"
                        testId="press-asset-headshot"
                    />
                </div>
            </section>

            {/* Quick-copy details */}
            <section className="mx-auto max-w-6xl px-6 md:px-10 pb-14">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="eyebrow text-gold-deep flex items-center gap-2">
                            <Radio className="w-3.5 h-3.5" /> Quick facts
                        </div>
                        <h2 className="mt-3 font-display text-3xl md:text-4xl">
                            Tap to <em className="italic gold-text">copy</em>.
                        </h2>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CopyableField label="Podcast title" value="Becoming H.E.R.: Breaking the Cycles" testId="press-copy-title" />
                    <CopyableField label="Host name" value="Coach Melissa" testId="press-copy-host" />
                    <CopyableField label="Tagline" value="Real conversations · Raw healing · Kingdom transformation" testId="press-copy-tagline" />
                    <CopyableField label="Website" value={SITE_URL} testId="press-copy-site" />
                    <CopyableField label="Booking email" value={CONTACT_EMAIL} testId="press-copy-email" />
                    <CopyableField label="Season 1 launch" value="July 4, 2026 · new episode every Tuesday" testId="press-copy-launch" />
                </div>
            </section>

            {/* Suggested interview angles */}
            <section className="mx-auto max-w-6xl px-6 md:px-10 pb-20">
                <div className="rounded-[32px] bg-white border border-lilac-200 p-8 md:p-12">
                    <div className="eyebrow text-gold-deep flex items-center gap-2">
                        <Heart className="w-3.5 h-3.5" /> Suggested angles
                    </div>
                    <h2 className="mt-3 font-display text-3xl md:text-4xl">
                        What Coach Melissa can <em className="italic gold-text">speak to</em>.
                    </h2>
                    <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-lilac-900">
                        {[
                            "Breaking generational cycles as a faith practice",
                            "Healing identity wounds & the names we picked up",
                            "The recovering people-pleaser's path to holy 'no'",
                            "Rebuilding self-worth after seasons of self-abandonment",
                            "Why 'shrinking' is fear dressed in scripture",
                            "How to walk into rooms as a daughter, not an imposter",
                            "Faith-based coaching for the woman still surviving",
                            "Building a purpose-driven brand as a woman of faith",
                        ].map((angle) => (
                            <li key={angle} className="flex items-start gap-3">
                                <span className="mt-1.5 grid place-items-center w-5 h-5 rounded-full bg-gold text-white shrink-0">
                                    <Crown className="w-3 h-3" />
                                </span>
                                <span className="text-sm leading-relaxed">{angle}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Signature speaking topics */}
            <SpeakingTopics onSelect={selectTopic} />

            {/* Speaker booking form */}
            <section id="booking" className="mx-auto max-w-6xl px-6 md:px-10 pb-20 scroll-mt-24">
                <BookingForm topic={selectedTopic} />
            </section>

            {/* Footer */}
            <footer className="border-t border-lilac-200/60 bg-white/50">
                <div className="mx-auto max-w-6xl px-6 md:px-10 py-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <div className="font-display text-xl text-lilac-900">
                            Becoming <em className="italic gold-text">H.E.R.</em>
                        </div>
                        <p className="text-xs text-lilac-700 mt-1">
                            &copy; {new Date().getFullYear()} Coach Melissa · Unshakable Purpose LLC
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <a
                            href={SITE_URL}
                            className="inline-flex items-center gap-2 text-sm text-lilac-700 hover:text-lilac-900"
                        >
                            <ExternalLink className="w-3.5 h-3.5" /> becomingherpod.org
                        </a>
                        <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            className="inline-flex items-center gap-2 text-sm text-lilac-700 hover:text-lilac-900"
                        >
                            <Mail className="w-3.5 h-3.5" /> {CONTACT_EMAIL}
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PressKit;
